/**
 * Contextual "what is this?" hint (QA F4). A small info button next to a
 * confusing control; pressing it explains the control inline. "Got it"
 * dismisses it for good (stored on the user, so it stays dismissed on every
 * device). Ids are a closed set: GUIDANCE_HINT_IDS in tour/tourModel.ts.
 */
import React, { useState } from 'react';
import { Pressable, StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { reviewDesk } from '../../design-system/theme';
import { useTutorial } from '../../context/TutorialContext';
import { useTranslation } from '../../hooks/useTranslation';
import type { GuidanceHintId } from '../../tour/tourModel';

interface InfoHintProps {
  id: GuidanceHintId;
  style?: StyleProp<ViewStyle>;
}

export function InfoHint({ id, style }: InfoHintProps) {
  const { t } = useTranslation();
  const { isHintVisible, openHint, dismissHint } = useTutorial();
  const [open, setOpen] = useState(false);

  if (!isHintVisible(id)) return null;

  const title = t(`guidanceHints.${id}.title`);

  return (
    <View style={[styles.wrap, style]} testID={`hint_${id}`}>
      <Pressable
        onPress={() => {
          if (!open) openHint(id);
          setOpen((value) => !value);
        }}
        accessibilityRole="button"
        accessibilityLabel={`${t('guidanceHints.open')} ${title}`}
        accessibilityState={{ expanded: open }}
        testID={`hint_${id}_toggle`}
        style={({ pressed }) => [styles.toggle, (open || pressed) && styles.toggleActive]}
      >
        <MaterialIcons name="help-outline" size={16} color={reviewDesk.accent} />
        <Text style={styles.toggleText}>{t('guidanceHints.open')}</Text>
      </Pressable>
      {open && (
        <View style={styles.panel} testID={`hint_${id}_panel`}>
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.body}>{t(`guidanceHints.${id}.body`)}</Text>
          <Pressable
            onPress={() => {
              setOpen(false);
              dismissHint(id);
            }}
            accessibilityRole="button"
            testID={`hint_${id}_dismiss`}
            style={styles.dismiss}
          >
            <Text style={styles.dismissText}>{t('guidanceHints.dismiss')}</Text>
          </Pressable>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    alignSelf: 'flex-start',
    gap: 6,
    maxWidth: 520,
  },
  toggle: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 4,
    minHeight: 28,
    paddingHorizontal: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
  },
  toggleActive: {
    backgroundColor: reviewDesk.accentSoft,
    borderColor: reviewDesk.accent,
  },
  toggleText: {
    color: reviewDesk.accent,
    fontSize: 12,
    fontWeight: '700',
  },
  panel: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.accentWash,
    padding: 12,
    gap: 6,
  },
  title: {
    color: reviewDesk.ink,
    fontSize: 13,
    fontWeight: '800',
  },
  body: {
    color: reviewDesk.inkSoft,
    fontSize: 13,
    lineHeight: 19,
  },
  dismiss: {
    alignSelf: 'flex-start',
    minHeight: 32,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: reviewDesk.accent,
    justifyContent: 'center',
  },
  dismissText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
  },
});
